/**
 * Click-to-Dial über die Telefonanlage: erst klingelt das eigene Telefon,
 * nach dem Abheben verbindet Placetel mit dem Ziel.
 *
 * Die Kennung hängt am Mandanten — wer für mehrere Standorte arbeitet, sitzt
 * dort an unterschiedlichen Telefonen. Deshalb wird je Mandant zwischengespeichert.
 * Ist nichts hinterlegt, bleibt es beim tel:-Link des Browsers.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { useTenant } from '@/contexts/TenantContext';

const cache = new Map();
const inFlight = new Map();
const listeners = new Set();

function publish(tenantId, config) {
  cache.set(tenantId, config);
  listeners.forEach((fn) => fn(tenantId, config));
}

async function loadConfig(tenantId) {
  if (cache.has(tenantId)) return cache.get(tenantId);
  if (!inFlight.has(tenantId)) {
    inFlight.set(
      tenantId,
      api
        .get('/telephony/dial/config')
        .then((r) => r.data || { available: false })
        .catch(() => ({ available: false }))
        .finally(() => inFlight.delete(tenantId)),
    );
  }
  publish(tenantId, await inFlight.get(tenantId));
  return cache.get(tenantId);
}

/** Nach Änderungen an den Kennungen, damit PhoneLinks sofort umschalten. */
export function refreshClickToDialConfig(tenantId) {
  if (tenantId) cache.delete(tenantId);
  else cache.clear();
  listeners.forEach((fn) => fn(tenantId, null));
}

export default function useClickToDial() {
  const { activeTenantId } = useTenant();
  const [config, setConfig] = useState(() => cache.get(activeTenantId) || null);

  useEffect(() => {
    if (!activeTenantId) {
      setConfig(null);
      return undefined;
    }
    const onChange = (tenantId) => {
      if (tenantId && tenantId !== activeTenantId) return;
      const known = cache.get(activeTenantId);
      if (known) setConfig(known);
      else loadConfig(activeTenantId).then(setConfig);
    };
    listeners.add(onChange);
    loadConfig(activeTenantId).then(setConfig);
    return () => listeners.delete(onChange);
  }, [activeTenantId]);

  const dial = useCallback(async (phone, name) => {
    try {
      await api.post('/telephony/dial', { phone, name: name || null });
      toast.success('Anruf wird aufgebaut — bitte am eigenen Telefon abheben.');
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Anruf konnte nicht aufgebaut werden.');
      return false;
    }
  }, []);

  return { available: !!config?.available, dial };
}
